import React from 'react';
import {Header, Icon} from 'semantic-ui-react';
import {gql} from '@apollo/client';
import {get as _get, isEmpty as _isEmpty} from 'lodash';

import {Link} from '@appComponents/Link';
import {Authorization} from '@appComponents/Authorization';
import {CircleButtonAdd} from '@appComponents/ButtonCollection';
import {convertToInt} from '@utils/helpers';
import {refetchQueryByName} from '@utils/apollo';
import LinkWithAuthorization from '@appComponents/LinkWithAuthorization';
import {SECURITY_PRIVILEGES_UPDATE} from '@constants/variables';
import {THIRD_PARTY_CLIENT_TUNNEL} from '@constants/resources';
import TunnelOption from '@modules/events/components/TunnelOption';
import {deleteEncodingTargetFromCdnConfiguration} from '@graphql/events/eventContentCdnConfiguration';
import {hideModal, showModalConfirmation} from '@utils/modal';
import {showMessageBox} from '@utils/messageBox';
import { RemoveThirdPartyTunnels } from '@graphql/encodingTarget/mutation';

import apolloClient from '../../../apolloClient';


export const renderAddButton = (eventId, contentId, authorization, linkName) => (
    <Authorization authorization={authorization}>
        <Link
            name={`events.configuration.content.${linkName}`}
            params={{
                id : eventId,
                contentId : contentId,
            }}>
            <CircleButtonAdd/>
        </Link>
    </Authorization>
);

export const parseTunnelerData = (data, vtdTunnels) => {
    return data.map(tunnel => ({
        ...tunnel,
        vtdTunnel: vtdTunnels?.find(vtdTunnel => convertToInt(vtdTunnel.id) === tunnel.vtd_tunnel_id),
    }));
};

export const showDeleteThirdPartyTunnelerModal = (encodingTargetsTunnels, rowData) => {
    showModalConfirmation({
        header: <Header icon='trash' content={'Remove third party tunneler push'}/>,
        text: 'Are you sure you want to remove this third party client tunnel for this encoding target?',
        onYes: () => deleteThirdPartyTunneler(encodingTargetsTunnels, rowData),
    });
};

export const parseCdnConfigurations = (data) => {
    if (_isEmpty(data)) return [];

    const getGroupKey = (row) =>
        `${_get(row, 'client_product.client.id', '')}|${_get(row, 'client_product.product.id', '')}|${row.name ?? ''}`;

    const getDistributionTypeKey = (row) =>
        `${getGroupKey(row)}|${_get(row, 'distribution_type.id', '')}`;

    const clientGroups = {};
    const distributionTypeGroups = {};
    const processedClientGroups = new Set();
    const processedDistributionTypeGroups = new Set();

    data.forEach(row => {
        const groupKey = getGroupKey(row);
        const typeKey = getDistributionTypeKey(row);

        clientGroups[groupKey] = (clientGroups[groupKey] || 0) + 1;
        distributionTypeGroups[typeKey] = (distributionTypeGroups[typeKey] || 0) + 1;
    });

    return [...data]
        .sort((a, b) => [
            _get(a, 'client_product.client.name', '').localeCompare(_get(b, 'client_product.client.name', '')),
            _get(a, 'client_product.product.name', '').localeCompare(_get(b, 'client_product.product.name', '')),
            (a.name ?? '').localeCompare(b.name ?? ''),
            _get(a, 'distribution_type.name', '').localeCompare(_get(b, 'distribution_type.name', '')),
            Number(a.encoding_target_id ?? 0) - Number(b.encoding_target_id ?? 0),
        ].find(v => 0 !== v) || 0)
        .map(row => {
            const groupKey = getGroupKey(row);
            const typeKey = getDistributionTypeKey(row);

            const isFirstClientGroup = !processedClientGroups.has(groupKey);
            const isFirstDistributionTypeGroup = !processedDistributionTypeGroups.has(typeKey);

            if (isFirstClientGroup) {
                processedClientGroups.add(groupKey);
            }

            if (isFirstDistributionTypeGroup) {
                processedDistributionTypeGroups.add(typeKey);
            }

            const createCell = (value, isFirst, rowSpan) => isFirst
                ? { rowSpan, value }
                : { hidden: true, value };

            return {
                id: { value: `${row.id}_${row.encoding_target_id}` },
                clientName: createCell(_get(row, 'client_product.client.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                productName: createCell(_get(row, 'client_product.product.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                cdnConfigurationName: createCell(row.name ?? '', isFirstClientGroup, clientGroups[groupKey]),
                cdnName: createCell(_get(row, 'cdn.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                liveIngestMethod: createCell(_get(row, 'live_ingest_method.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                recordingIngestMethod: createCell(_get(row, 'recording_ingest_method.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                akamaiCdnIngestMethodName: createCell(_get(row, 'akamai_cdn_ingest_method.name', ''), isFirstClientGroup, clientGroups[groupKey]),
                distributionTypeName: createCell(_get(row, 'distribution_type.name', ''), isFirstDistributionTypeGroup, distributionTypeGroups[typeKey]),
                cdnId: { value: row.id },
                encodingTargetId: { value: row.encoding_target_id ?? '' },
            };
        });
};

export const deleteThirdPartyTunneler = (encodingTargetsTunnels, rowData) => {
    apolloClient.mutate({
        mutation: RemoveThirdPartyTunnels,
        refetchQueries: ['GetEventContentEncodingConfigurationChanged', 'GetEncodingTargetsForDistributions'],
        variables: {
            id: convertToInt(rowData.encoding_target_id),
            third_party_client_tunnels: getFilteredThirdPartyTunnelsIds(encodingTargetsTunnels, rowData),
        },
    }).then(() => {
        hideModal();
        showMessageBox(
            'eventsConfiguration',
            'The third party tunneler push has been removed successfully for the given target.',
            null,
            'success'
        );
        refetchQueryByName('encodingTargetsQuery');
        refetchQueryByName('GetEncodingTargetsForDistributions');
        refetchQueryByName('GetThirdPartyClientTunnelsForEventContent');
        refetchQueryByName('GetThirdPartyClientsForList');
        refetchQueryByName('GetEncodingTargetThirdPartyVodStoragesData');
        refetchQueryByName('GetEventContentEncodingConfigurationChanged');
    }).catch((error) => {
        hideModal();
        showMessageBox(
            'eventsConfiguration',
            'The third party tunneler push could not be removed for the given target.',
            `${error}`,
            'error'
        );
    });
};

export const showDeleteThirdPartyCdnConfigurationModal = (eventContentThirdPartyClientCdnConfigurations, rowData) => {
    const cdnId = rowData.cdnId.value;
    const encodingTargetId = rowData.encodingTargetId.value;

    showModalConfirmation({
        header: <Header icon='trash' content={'Remove CDN configuration'}/>,
        text: 'Are you sure you want to remove this CDN configuration for this target?',
        onYes: () => deleteCdnConfiguration(cdnId, encodingTargetId, eventContentThirdPartyClientCdnConfigurations),
    });
};

export const deleteCdnConfiguration = (cdnConfigurationId, encodingTargetId, eventContentThirdPartyClientCdnConfigurations) => {
    showModalConfirmation({
        text: 'Removing the CDN configuration for the given target...',
    });

    const cdnConfigurationIds = eventContentThirdPartyClientCdnConfigurations
        .filter(item => convertToInt(item.encoding_target_id) === convertToInt(encodingTargetId))
        .filter(item => convertToInt(item.id) !== convertToInt(cdnConfigurationId))
        .map(item => convertToInt(item.id));

    apolloClient.mutate({
        mutation: gql(deleteEncodingTargetFromCdnConfiguration),
        refetchQueries: ['GetThirdPartyClientCdnConfigurationsForEventContent', 'GetEventContentEncodingConfigurationChanged', 'GetEncodingTargetsForDistributions'],
        variables: {
            id: convertToInt(encodingTargetId), client_cdn_configurations: cdnConfigurationIds,
        },
    }).then(() => {
        hideModal();
        showMessageBox('eventsConfiguration', 'The CDN configuration has been removed successfully for the given target.', null, 'success');
    }).catch((error) => {
        hideModal();
        showMessageBox('eventsConfiguration', 'The CDN configuration could not be removed for the given target.', `${error}`, 'error');
    });
};

export const getFilteredThirdPartyTunnelsIds = (encodingTargetsTunnels, rowData) => {
    return encodingTargetsTunnels
        .filter(tunnel =>
            convertToInt(tunnel.encoding_target_id) === convertToInt(rowData.encoding_target_id) &&
            convertToInt(tunnel.id) !== convertToInt(rowData.id)
        )
        .map(tunnel => convertToInt(tunnel.id));
};

export const parseClientProductsForDropdown = (data) => {
    return data.map((product) => ({
        key: convertToInt(product.client_product.client.id),
        text: product.client_product.client.name,
        value: convertToInt(product.client_product.client.id),
    }));
};

export const renderTunnelerLink = (row) => {
    return (
        <div>
            <LinkWithAuthorization
                link={{
                    name: 'clients.products.thirdParty.tunnels.edit',
                    params: {
                        id: row?.client?.id,
                        thirdPartyClientTunnelId: row.id,
                    },
                }}
                privileges={SECURITY_PRIVILEGES_UPDATE}
                resources={THIRD_PARTY_CLIENT_TUNNEL}
            >
                {row.name}
            </LinkWithAuthorization>
            {row.is_placeholder_for_auto_assignment && <Icon name='cogs' className='--paddingLeft-0-5rem' />}
        </div>
    );
};

export const generateTunnelDropdownOptions = (filteredTunnels, vtdTunnels) => {
    const dropdownOptions = [];

    filteredTunnels.forEach((tunnel) => {
        const vtdTunnel = vtdTunnels.find(vtdTunnel => convertToInt(vtdTunnel.id) === tunnel.vtd_tunnel_id);

        if (tunnel.vtd_tunnel_id) {
            dropdownOptions.push({
                key: convertToInt(tunnel.id),
                text: <TunnelOption tunnel={tunnel} vtdTunnel={vtdTunnel} />,
                value: convertToInt(tunnel.id),
            });
        } else {
            dropdownOptions.push({
                key: convertToInt(tunnel.id),
                text: tunnel.name,
                value: convertToInt(tunnel.id),
            });
        }
    });

    return dropdownOptions;
};
